import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
const PREFIX = "grain-saas:consolidation:view:v1:";
const memory = new Map();
// Préférences non chargées ou stockage bloqué : le stockage ne prévient personne, les composants suivent ce compteur.
const listeners = new Set();
let version = 0;
export const subscribeConsolidationView = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export const consolidationViewVersion = () => version;
// Dernière journée lue, périodes d'heures et mois du calendrier déjà vus : réaffichés aussitôt au retour sur la page,
// le temps de les recharger. Oubliés ensemble au changement de session.
let lastDay = null;
export const consolidationHoursCache = new Map();
export const consolidationCalendarCache = new Map();
export const rememberConsolidationDay = (period, data) => { lastDay = { period, data }; };
export const lastConsolidationDay = period => lastDay?.period === period ? lastDay.data : null;
export const forgetConsolidationDay = () => { lastDay = null; consolidationHoursCache.clear(); consolidationCalendarCache.clear(); };

export function readConsolidationView(scope = "") {
  try {
    const raw = userUiPreferenceStorage("session").getItem(PREFIX + scope);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch { /* Storage can be disabled by browser settings. */ }
  return memory.get(scope) || {};
}

export function saveConsolidationView(scope, patch) {
  const previous = readConsolidationView(scope), next = { ...previous, ...patch };
  memory.set(scope, next);
  try { userUiPreferenceStorage("session").setItem(PREFIX + scope, JSON.stringify(next)); } catch { /* Memory fallback. */ }
  if (JSON.stringify(next) === JSON.stringify(previous)) return;
  version += 1;
  listeners.forEach(listener => listener());
}

// La fermeture du Gantt vaut pour la session courante, y compris après un rechargement.
export function resetConsolidationGeotabGantt() {
  const scopes = new Set(memory.keys());
  try {
    for (let index = 0; index < userUiPreferenceStorage("session").length; index++) {
      const key = userUiPreferenceStorage("session").key(index);
      if (key?.startsWith(PREFIX)) scopes.add(key.slice(PREFIX.length));
    }
  } catch { /* Le cache mémoire reste utilisable sans stockage navigateur. */ }
  for (const scope of scopes) saveConsolidationView(scope, { geotab_gantt: true });
}

export function isValidConsolidationDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
}

// Journées always shows one day; Prestations and Heures show a period (the anchor day's week by default).
// `day` is the day shared by the three tabs: it stays highlighted in the period views.
export function resolveConsolidationParams(params, saved = {}, today, durable = {}) {
  const explicitFrom = params.get("from_date") || params.get("date");
  const hasExplicitDates = params.has("from_date") || params.has("date") || params.has("to_date");
  const durableFrom = durable.last_consolidation_from_date;
  const anchor = isValidConsolidationDate(explicitFrom) ? explicitFrom
    : !hasExplicitDates && isValidConsolidationDate(durableFrom) ? durableFrom
      : !hasExplicitDates && isValidConsolidationDate(saved.from) ? saved.from : today;
  const requestedView = params.get("view") || saved.view;
  const view = ["summary", "hours"].includes(requestedView) ? requestedView : "sheets";
  const requestedDay = params.get("day");
  if (view === "sheets") return { from: anchor, to: anchor, view, day: anchor };
  const week = consolidationWeek(view === "hours" && !hasExplicitDates ? today : anchor);
  const from = isValidConsolidationDate(explicitFrom) ? explicitFrom : week.from;
  const explicitTo = params.get("to_date");
  const to = isValidConsolidationDate(explicitTo) && explicitTo >= from ? explicitTo
    : isValidConsolidationDate(explicitFrom) && hasExplicitDates ? from : week.to;
  const day = isValidConsolidationDate(requestedDay) && requestedDay >= from && requestedDay <= to ? requestedDay
    : anchor >= from && anchor <= to ? anchor : from;
  return { from, to, view, day };
}

export function consolidationMonth(day) {
  const [year, month] = day.split("-").map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const prefix = `${year}-${String(month).padStart(2, "0")}`;
  return { from: `${prefix}-01`, to: `${prefix}-${String(last).padStart(2, "0")}` };
}

export function consolidationPeriod(mode, day) {
  return mode === "week" ? consolidationWeek(day) : mode === "month" ? consolidationMonth(day) : { from: day, to: day };
}

export function consolidationPeriodMode(from, to) {
  if (from === to) return "day";
  for (const mode of ["week", "month"]) {
    const period = consolidationPeriod(mode, from);
    if (period.from === from && period.to === to) return mode;
  }
  return "custom";
}

export function shiftConsolidationDay(day, mode, step) {
  const date = new Date(`${day}T12:00:00Z`);
  if (mode === "month") date.setUTCMonth(date.getUTCMonth() + step, 1);
  else date.setUTCDate(date.getUTCDate() + step * (mode === "week" ? 7 : 1));
  return date.toISOString().slice(0, 10);
}

export function consolidationWeek(day) {
  const start = new Date(`${day}T12:00:00Z`);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

export function consolidationEditorKey(row) {
  return `${row.dispatch_date || row.delivery_date}:${row.dispatch_period || "day"}:${row.source_key || row.id}`;
}
